import { useState, useEffect } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { KeyRound, Eye, EyeOff, AlertCircle } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { setSession } = useAuth();
  const [token, setToken] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    const t = params.get("token");
    if (!t) setMissing(true);
    else setToken(t);
  }, [params]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) return toast.error("Password must be at least 8 characters.");
    if (password !== confirm) return toast.error("Passwords don't match.");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, password });
      if (setSession && data.token && data.user) {
        setSession(data.token, data.user);
      }
      toast.success("Password updated ✨");
      nav("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not reset password. The link may have expired.");
    } finally {
      setBusy(false);
    }
  };

  if (missing) {
    return (
      <div className="container-narrow py-20 max-w-md mx-auto" data-testid="reset-page-missing">
        <div className="card-soft p-8 text-center">
          <AlertCircle className="w-8 h-8 text-marigold mx-auto mb-3" />
          <h2 className="font-heading text-2xl font-semibold mb-2">Missing reset link</h2>
          <p className="text-sm text-ink2 mb-4">
            This page needs a reset token in the URL. Start over from the login page.
          </p>
          <Link to="/forgot-password" className="btn-primary text-sm" data-testid="reset-restart">
            Request a new link
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="container-narrow py-20 grid lg:grid-cols-2 gap-12 items-center" data-testid="reset-page">
      <div className="hidden lg:block">
        <div className="label-eyebrow mb-3">Password reset</div>
        <h1 className="font-heading text-5xl font-semibold leading-tight mb-4">
          Pick something you&apos;ll <span className="italic text-marigold">actually remember</span>.
        </h1>
        <p className="text-ink2 max-w-md">
          8 characters minimum. A short sentence with a number and a symbol is much stronger than
          Password123. And it stays yours — we never see it in plaintext.
        </p>
      </div>

      <form onSubmit={submit} className="card-soft p-8 max-w-md w-full mx-auto" data-testid="reset-form">
        <div className="w-11 h-11 rounded-2xl bg-marigold/15 text-marigold flex items-center justify-center mb-4">
          <KeyRound className="w-5 h-5" />
        </div>
        <h2 className="font-heading text-2xl font-semibold mb-2">Set a new password</h2>
        <p className="text-sm text-ink2 mb-6">
          Enter it twice to make sure. We&apos;ll sign you in as soon as it&apos;s saved.
        </p>

        <label className="text-sm font-medium block mb-1">New password</label>
        <div className="relative mb-4">
          <input
            type={show ? "text" : "password"}
            required
            minLength={8}
            value={password}
            onChange={e => setPassword(e.target.value)}
            className="input-soft pr-10"
            placeholder="At least 8 characters"
            data-testid="reset-password"
          />
          <button
            type="button"
            onClick={() => setShow(s => !s)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-ink2 hover:text-ink"
            aria-label={show ? "Hide password" : "Show password"}
            data-testid="reset-toggle-visibility"
          >
            {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>

        <label className="text-sm font-medium block mb-1">Confirm new password</label>
        <input
          type={show ? "text" : "password"}
          required
          minLength={8}
          value={confirm}
          onChange={e => setConfirm(e.target.value)}
          className="input-soft mb-6"
          placeholder="Same again"
          data-testid="reset-confirm"
        />

        <button className="btn-primary w-full justify-center" disabled={busy} data-testid="reset-submit">
          {busy ? "Saving…" : "Save & sign in"}
        </button>

        <div className="text-center mt-4">
          <Link to="/login" className="text-sm text-ink2 hover:text-marigold" data-testid="reset-back-login">
            Back to log in
          </Link>
        </div>
      </form>
    </div>
  );
}
